/*  ========================================================================
    # Components - Mobile Menu (Experimental)
    ========================================================================  */
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import useLocalization from '../../hooks/useLocalization';
import useTranslation from '../../hooks/useTranslation';
import { AnimatePresence, motion } from 'motion/react';
import RichMenuSlider from '../RichMenuSlider/RichMenuSlider';

const MobileMenu = ({ menuItems }) => {
  const __ = useTranslation();
  const locale = useLocalization();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMainItemId, setActiveMainItemId] = useState(null);
  const [filteredMenuItems, setFilteredMenuItems] = useState(menuItems);
  const iconChevronRight = useMemo(() => locale('svgSprite') + '#chevron-right', [locale]);

  const toggleMenu = useCallback(
    open => {
      setMenuOpen(open);

      // Reset active main item id
      if (!open) setActiveMainItemId(null);

      setTimeout(() => {
        document.body.style.overflow = open ? 'hidden' : 'auto';
      }, 300);

      if (open) {
        document.querySelector('.c-header__wrapper').scrollIntoView({ top: 0, behavior: 'smooth' });
      }
    },
    [setMenuOpen, setTimeout]
  );

  const toggleSubMenu = useCallback(
    newId => {
      setActiveMainItemId(lastId => (newId === lastId ? null : newId));
    },
    [setActiveMainItemId]
  );

  useEffect(() => {
    if (activeMainItemId === null) {
      setFilteredMenuItems(menuItems);
    } else {
      setFilteredMenuItems(menuItems.filter(item => item.id === activeMainItemId));
    }
  }, [menuItems, activeMainItemId]);

  return (
    <>
      <button
        type='button'
        className={`c-header__menu-toggle ${menuOpen ? 'active' : ''}`}
        title={__('toggle-menu')}
        aria-label={__('toggle-menu')}
        onClick={() => toggleMenu(!menuOpen)}
      >
        <span aria-hidden='true'></span>
        <span aria-hidden='true'></span>
        <span aria-hidden='true'></span>
      </button>
      <AnimatePresence mode='wait'>
        {menuOpen && (
          <motion.div
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: menuOpen ? 0 : '-100%', opacity: menuOpen ? 1 : 0 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{ type: 'tween', duration: 0.3 }}
            className={`c-header__mobile-menu`}
          >
            <motion.ul
              className='c-header__mobile-nav'
              initial='out'
              animate={activeMainItemId === null ? 'level_1' : 'level_2'}
              exit='out'
              transition={{ delayChildren: 0.2, staggerChildren: 0.1 }}
            >
              <AnimatePresence mode='popLayout'>
                {filteredMenuItems.map(item => (
                  <motion.li
                    className='c-header__mobile-entry'
                    key={item.id}
                    layout='position'
                    variants={{
                      level_1: { opacity: 1, x: 0 },
                      level_2: { opacity: 1, x: 0, transition: { when: 'beforeChildren' } },
                      out: { opacity: 0, x: -20 }
                    }}
                  >
                    <div className='c-header__mobile-item'>
                      {item.items.length > 0 ? (
                        <>
                          <button
                            className='c-header__mobile-expand-toggle'
                            title={(activeMainItemId === item.id
                              ? __('mobile-menu-collapse')
                              : __('mobile-menu-expand')
                            ).replace('%s', item.title)}
                            onClick={() => toggleSubMenu(item.id)}
                          >
                            <motion.svg
                              viewBox='0 0 20 20'
                              aria-hidden='true'
                              animate={{
                                rotate: activeMainItemId === item.id ? 180 : 0
                              }}
                            >
                              <use href={iconChevronRight}></use>
                            </motion.svg>
                          </button>
                          <a className='h4' href={item.url} target='_self' title={item.title}>
                            {item.title}
                          </a>
                        </>
                      ) : (
                        <a className='h4' href={item.url} target='_self' title={item.title}>
                          {item.title}
                        </a>
                      )}
                    </div>
                    {activeMainItemId === item.id && item.items.length > 0 && (
                      <motion.div
                        transition={{ delayChildren: 0.2, staggerChildren: 0.1 }}
                        variants={{
                          out: { opacity: 0 },
                          level_1: { opacity: 0 },
                          level_2: { opacity: 1 }
                        }}
                      >
                        <motion.ul className='c-header__mobile-submenu'>
                          {item.items.map(subItem => (
                            <motion.li
                              className='c-header__mobile-subitem'
                              key={`${item.id}_${subItem.id}`}
                              variants={{
                                out: { opacity: 0, x: 20 },
                                level_1: { opacity: 0, x: 20 },
                                level_2: { opacity: 1, x: 0 }
                              }}
                            >
                              <a href={subItem.url} target='_self' title={subItem.title}>
                                {subItem.title}
                              </a>
                            </motion.li>
                          ))}
                        </motion.ul>
                        {item.richMenuCards.length > 0 && item.isRichMenu && (
                          <motion.div
                            variants={{
                              out: { opacity: 0 },
                              level_1: { opacity: 0 },
                              level_2: { opacity: 1 }
                            }}
                            className='c-header__mobile-cards'
                          >
                            <RichMenuSlider
                              type='menu'
                              readMoreLink={item.richMenuLink}
                              intro={item.richMenuIntro}
                              cards={item.richMenuCards}
                            />
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </motion.li>
                ))}
              </AnimatePresence>
            </motion.ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

MobileMenu.propTypes = {
  menuItems: PropTypes.array
};

export default MobileMenu;
